import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { qrAdminListRoomsFn, qrAdminDeleteRoomFn, qrAdminGenerateQRDataFn, auth } from "../firebase";
import { FUNCTION_URLS } from "../config/functions";
import { vibrate } from "../utils/platform";
import { AdminRoom } from "../types";
import "../styles/main.scss";

interface AdminPageProps {
  user: any;
}

export function AdminPage({ user }: AdminPageProps) {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState<AdminRoom[]>([]);
  const [codes, setCodes] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showQR, setShowQR] = useState<string | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>("");
  
  // Admin authentication
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  
  // Create room form
  const [newCode, setNewCode] = useState("");
  const [newLat, setNewLat] = useState("");
  const [newLng, setNewLng] = useState("");
  const [newRadius, setNewRadius] = useState("100");
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");

  // Check if user is already authenticated with email (not anonymous)
  useEffect(() => {
    if (user && !user.isAnonymous && user.email) {
      setIsAdminAuthenticated(true);
      fetchRooms();
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError(null);
    
    try {
      await signInWithEmailAndPassword(auth, adminEmail, adminPassword);
      setIsAdminAuthenticated(true);
      setAdminEmail("");
      setAdminPassword("");
      fetchRooms();
    } catch (error: any) {
      console.error("Admin login failed:", error);
      setAuthError(error.message || "Invalid email or password");
    } finally {
      setAuthLoading(false);
    }
  };

  const handleAdminLogout = async () => {
    try {
      await signOut(auth);
      setIsAdminAuthenticated(false);
      navigate('/');
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const fetchRooms = async () => {
    setLoading(true);
    try {
      const result: any = await qrAdminListRoomsFn();
      setRooms(result.rooms || []);
      setCodes(result.codes || []);
    } catch (err) {
      console.error("Failed to load rooms:", err);
      alert("Failed to load rooms");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRoom = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newCode || !newLat || !newLng) {
      alert("Please fill in all fields");
      return;
    }

    if (!/^\d{8}$/.test(newCode)) {
      alert("Code must be 8 digits");
      return;
    }

    const lat = parseFloat(newLat);
    const lng = parseFloat(newLng);
    
    if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      alert("Invalid coordinates");
      return;
    }

    setCreating(true);
    vibrate(10);

    try {
      if (!user) {
        throw new Error("User must be authenticated");
      }

      const token = await user.getIdToken();
      
      const response = await fetch(FUNCTION_URLS.qrCreateCodePublic, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': 'blinkbli',
        },
        body: JSON.stringify({
          code: newCode,
          location: { lat, lng },
          radiusMeters: parseInt(newRadius),
          ...(newName.trim() && { name: newName.trim() }),
          ...(newDescription.trim() && { description: newDescription.trim() }),
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to create room');
      }

      alert(`Room ${newCode} created successfully!`);
      setNewCode("");
      setNewLat("");
      setNewLng("");
      setNewRadius("100");
      setNewName("");
      setNewDescription("");
      await fetchRooms();
    } catch (err: any) {
      console.error("Failed to create room:", err);
      alert(`Failed to create room: ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteRoom = async (code: string) => {
    if (!confirm(`Are you sure you want to delete room ${code}?`)) {
      return;
    }

    vibrate(15);
    try {
      await qrAdminDeleteRoomFn({ code });
      alert(`Room ${code} deleted`);
      await fetchRooms();
    } catch (err: any) {
      console.error("Failed to delete room:", err);
      alert(`Failed to delete room: ${err.message}`);
    }
  };

  const handleGenerateQR = async (code: string) => {
    vibrate(10);
    try {
      const result: any = await qrAdminGenerateQRDataFn({ code });
      
      // Generate QR code using a public API
      const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(result.qrUrl)}`;
      
      setQrDataUrl(qrApiUrl);
      setShowQR(code);
    } catch (err: any) {
      console.error("Failed to generate QR:", err);
      alert(`Failed to generate QR: ${err.message}`);
    }
  };

  const handleUseCurrentLocation = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setNewLat(position.coords.latitude.toFixed(6));
        setNewLng(position.coords.longitude.toFixed(6));
      },
      (error) => {
        alert("Failed to get location: " + error.message);
      }
    );
  };

  const generateRandomCode = () => {
    const code = Math.floor(10000000 + Math.random() * 90000000).toString();
    setNewCode(code);
  };

  // Merge rooms and codes data
  const roomsWithLocation = rooms.map(room => {
    const codeData = codes.find(c => c.code === room.code);
    return {
      ...room,
      location: codeData?.location,
      radiusMeters: codeData?.radiusMeters,
    };
  });

  // Show login form if not authenticated
  if (!isAdminAuthenticated) {
    return (
      <div className="admin-page">
        <header className="admin-page__header">
          <button onClick={() => navigate('/')} className="admin-page__back-button">
            ← Back to Home
          </button>
          <h1>🔧 Admin Panel</h1>
          <div className="admin-page__spacer" />
        </header>

        <div className="admin-page__content">
          <div className="admin-login">
            <h2>Admin Login</h2>
            <p className="admin-login__desc">
              Sign in with your admin email and password to manage rooms.
            </p>
            
            <form onSubmit={handleAdminLogin} className="admin-login__form">
              <input
                type="email"
                placeholder="Admin Email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                className="admin-login__input"
                required
                disabled={authLoading}
              />
              
              <input
                type="password"
                placeholder="Password"
                value={adminPassword}
                onChange={(e) => setAdminPassword(e.target.value)}
                className="admin-login__input"
                required
                disabled={authLoading}
              />
              
              {authError && (
                <div className="admin-login__error">
                  ⚠️ {authError}
                </div>
              )}
              
              <button 
                type="submit" 
                disabled={authLoading || !adminEmail || !adminPassword}
                className="admin-login__button"
              >
                {authLoading ? "Signing in..." : "Sign In as Admin"}
              </button>
            </form>

            <p className="admin-login__note">
              Guests can use the app without signing in. Only admins need to log in to manage rooms.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <header className="admin-page__header">
        <button onClick={() => navigate('/')} className="admin-page__back-button">
          ← Back to Home
        </button>
        <h1>🔧 Admin Panel</h1>
        <div className="admin-page__user">
          <span className="admin-page__email">{user?.email}</span>
          <button onClick={handleAdminLogout} className="admin-page__logout-button">
            Logout
          </button>
        </div>
      </header>

      <div className="admin-page__content">
        {/* Create Room Form */}
        <section className="admin-section">
          <h2>Create New Room</h2>
          <form onSubmit={handleCreateRoom} className="admin-form">
            <div className="admin-form__row">
              <input
                type="text"
                placeholder="8-digit code"
                value={newCode}
                onChange={(e) => setNewCode(e.target.value)}
                maxLength={8}
                pattern="\d{8}"
                className="admin-form__input"
                required
              />
              <button 
                type="button" 
                onClick={generateRandomCode}
                className="admin-form__button admin-form__button--secondary"
              >
                Random
              </button>
            </div>

            <div className="admin-form__row">
              <input
                type="number"
                step="any"
                placeholder="Latitude"
                value={newLat}
                onChange={(e) => setNewLat(e.target.value)}
                className="admin-form__input"
                required
              />
              <input
                type="number"
                step="any"
                placeholder="Longitude"
                value={newLng}
                onChange={(e) => setNewLng(e.target.value)}
                className="admin-form__input"
                required
              />
              <button 
                type="button" 
                onClick={handleUseCurrentLocation}
                className="admin-form__button admin-form__button--secondary"
              >
                📍 Current
              </button>
            </div>

            <div className="admin-form__row">
              <input
                type="number"
                placeholder="Radius (meters)"
                value={newRadius}
                onChange={(e) => setNewRadius(e.target.value)}
                min="10"
                max="1000"
                className="admin-form__input"
                required
              />
            </div>

            <div className="admin-form__row">
              <input
                type="text"
                placeholder="Title (optional, max 100 chars)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={100}
                className="admin-form__input"
              />
            </div>

            <div className="admin-form__row">
              <textarea
                placeholder="Description (optional, max 500 chars)"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                maxLength={500}
                rows={3}
                className="admin-form__input"
              />
            </div>

            <button 
              type="submit" 
              disabled={creating}
              className="admin-form__button admin-form__button--primary"
            >
              {creating ? "Creating..." : "Create Room"}
            </button>
          </form>
        </section>

        {/* Rooms List */}
        <section className="admin-section">
          <div className="admin-section__header">
            <h2>Rooms ({roomsWithLocation.length})</h2>
            <button 
              onClick={fetchRooms} 
              className="admin-section__refresh-button"
              disabled={loading}
              aria-label="Refresh rooms"
            >
              ↻
            </button>
          </div>

          {loading ? (
            <div className="admin-section__loading">Loading...</div>
          ) : roomsWithLocation.length === 0 ? (
            <div className="admin-section__empty">No rooms yet</div>
          ) : (
            <div className="admin-rooms">
              {roomsWithLocation.map((room) => (
                <div key={room.code} className="admin-room">
                  <div className="admin-room__info">
                    <div className="admin-room__code">Room {room.code}</div>
                    <div className="admin-room__meta">
                      <span>{room.numberOfUsers} members</span>
                      <span>•</span>
                      <span>{room.messageOrder} messages</span>
                      {room.location && (
                        <>
                          <span>•</span>
                          <span>{room.location.lat.toFixed(4)}, {room.location.lng.toFixed(4)}</span>
                          <span>•</span>
                          <span>{room.radiusMeters}m radius</span>
                        </>
                      )}
                    </div>
                    <div className="admin-room__date">
                      Created: {new Date(room.createdAt).toLocaleString()}
                    </div>
                  </div>
                  <div className="admin-room__actions">
                    <button 
                      onClick={() => handleGenerateQR(room.code)}
                      className="admin-room__button admin-room__button--qr"
                    >
                      QR
                    </button>
                    <button 
                      onClick={() => handleDeleteRoom(room.code)}
                      className="admin-room__button admin-room__button--delete"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* QR Code Modal */}
        {showQR && (
          <div className="qr-modal">
            <div className="qr-modal__overlay" onClick={() => setShowQR(null)} />
            <div className="qr-modal__content">
              <h3>QR Code for Room {showQR}</h3>
              {qrDataUrl && (
                <img 
                  src={qrDataUrl} 
                  alt={`QR Code for room ${showQR}`}
                  className="qr-modal__image"
                />
              )}
              <div className="qr-modal__actions">
                <a 
                  href={qrDataUrl} 
                  download={`room-${showQR}.png`}
                  className="qr-modal__button qr-modal__button--primary"
                >
                  Download
                </a>
                <button 
                  onClick={() => setShowQR(null)}
                  className="qr-modal__button qr-modal__button--secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

